/**
 * Print-only Si Hua activation card atom for Monthly Consultation PDF.
 * Canva page-04 layout: soft role pill top-left, palace top-right, English
 * star title, body, tinted action foot. Chinese characters are omitted.
 */

import React from "react";
import {
  bodyFontFamily,
  cardPadding,
  cardRadius,
  charcoal,
  displayFontFamily,
  mutedGray,
  siHuaJi,
  siHuaKe,
  siHuaLu,
  siHuaQuan,
} from "../pdfTheme";
import { PdfCard } from "./PdfCard";
import { PdfPill } from "./PdfPill";

/**
 * Si Hua activation role used for accent and pill colors.
 * lead = 化忌, support = 化权, recognition = 化科, resource = 化禄.
 */
export type PdfSiHuaRole = "lead" | "support" | "recognition" | "resource";

/**
 * Props for one Si Hua / stem activation card.
 */
export interface PdfSiHuaCardProps {
  /** Activation role driving accent and pill colors. */
  role: PdfSiHuaRole;
  /** Palace name where the activation lands. */
  palace: string;
  /** English star name. */
  starName: string;
  /** Chinese star characters (accepted for API; not rendered on print). */
  starChinese: string;
  /** Coaching / mechanism body copy. */
  body: string;
  /** Concrete action line for the activation. */
  action: string;
}

/** Role → accent color map (Si Hua theme tokens). */
const ROLE_ACCENT: Record<PdfSiHuaRole, string> = {
  lead: siHuaJi,
  support: siHuaQuan,
  recognition: siHuaKe,
  resource: siHuaLu,
};

/** Role → uppercase pill label (Canva page-04). */
const ROLE_LABEL: Record<PdfSiHuaRole, string> = {
  lead: "Lead Signal",
  support: "Support",
  recognition: "Recognition",
  resource: "Resource",
};

/**
 * Soft tint background for pills / action bars (~12% alpha via hex suffix).
 *
 * @param color - Theme hex color
 * @returns Light background tint
 */
function roleTintBg(color: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    return `${color}1f`;
  }
  return `${mutedGray}1f`;
}

/**
 * Type guard for {@link PdfSiHuaRole}.
 *
 * @param value - Candidate role string
 * @returns True when value is a known Si Hua role
 */
function isPdfSiHuaRole(value: string): value is PdfSiHuaRole {
  return (
    value === "lead" ||
    value === "support" ||
    value === "recognition" ||
    value === "resource"
  );
}

/**
 * Compact Canva-style activation card: pill + palace, title, body, action bar.
 * Content-sized (not stretched) so the 2×2 matches Canva page-04 density.
 */
export const PdfSiHuaCard: React.FC<PdfSiHuaCardProps> = ({
  role,
  palace,
  starName,
  starChinese,
  body,
  action,
}) => {
  const safeRole: PdfSiHuaRole = isPdfSiHuaRole(role) ? role : "recognition";
  const accent = ROLE_ACCENT[safeRole];
  const safePalace = typeof palace === "string" ? palace.trim() : "";
  const safeStarName = typeof starName === "string" ? starName.trim() : "";
  const safeBody = typeof body === "string" ? body.trim() : "";
  const safeAction = typeof action === "string" ? action.trim() : "";

  /*
   * starChinese is accepted for API compatibility; Activations print is
   * English-only and never appends Chinese to the title.
   */
  void starChinese;

  const starLine = safeStarName;

  return (
    <PdfCard accentColor={accent}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <PdfPill
          label={ROLE_LABEL[safeRole]}
          color={accent}
          bg={roleTintBg(accent)}
        />
        {safePalace.length > 0 ? (
          <span
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              fontFamily: bodyFontFamily,
              fontSize: 10,
              fontWeight: 400,
              letterSpacing: "0.02em",
              color: mutedGray,
              textAlign: "right",
              lineHeight: 1.4,
            }}
          >
            {safePalace}
          </span>
        ) : null}
      </div>

      {starLine.length > 0 ? (
        <h4
          style={{
            margin: "0 0 6px",
            fontFamily: displayFontFamily,
            fontSize: 14,
            fontWeight: 700,
            color: charcoal,
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
            textTransform: "uppercase",
          }}
        >
          {starLine}
        </h4>
      ) : null}

      {safeBody.length > 0 ? (
        <p
          style={{
            margin: safeAction.length > 0 ? "0 0 10px" : 0,
            fontFamily: bodyFontFamily,
            fontSize: 11,
            lineHeight: 1.5,
            color: mutedGray,
          }}
        >
          {safeBody}
        </p>
      ) : null}

      {safeAction.length > 0 ? (
        <div
          style={{
            marginLeft: -cardPadding,
            marginRight: -cardPadding,
            marginBottom: -cardPadding,
            padding: "9px 14px",
            backgroundColor: roleTintBg(accent),
            borderBottomLeftRadius: cardRadius,
            borderBottomRightRadius: cardRadius,
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: bodyFontFamily,
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1.45,
              color: accent,
            }}
          >
            {safeAction}
          </p>
        </div>
      ) : null}
    </PdfCard>
  );
};
